/**
 * Global Socket.IO client: connects with auth, subscribes to balance/order/price events,
 * and drives real-time UI updates. REST remains fallback and for initial loads.
 *
 * Financial refresh protections: refresh lock, debounce, rate limit, user validation,
 * hidden tab optimization, reconnect recovery, visibility recovery, cleanup safety.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { API_BASE_URL } from "../config/api";
import { useAuth } from "./AuthContext";
import { SOCKET_EVENTS, createListeners } from "../socket";

const SocketContext = createContext({
  connected: false,
  lastOrderUpdate: null,
  getPriceTick: () => null,
  subscribeProperty: () => {},
  unsubscribeProperty: () => {},
});

function getSocketUrl() {
  const base = (API_BASE_URL || "").replace(/\/$/, "");
  if (!base) return null;
  try {
    const u = new URL(base);
    return `${u.protocol}//${u.host}`;
  } catch {
    return base;
  }
}

export function SocketProvider({ children }) {
  const { user, fetchMe, refreshFinancial } = useAuth();
  const [connected, setConnected] = useState(false);
  const [socket, setSocket] = useState(null);
  const [lastOrderUpdate, setLastOrderUpdate] = useState(null);
  const [priceTicks, setPriceTicks] = useState({});
  const socketRef = useRef(null);
  const subscribedPropertyRef = useRef(null);
  const propertyHandlersRef = useRef({});

  const refreshTimerRef = useRef(null);
  const refreshingRef = useRef(false);
  const lastRefreshRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeRefreshFinancial = useCallback(async () => {
    const now = Date.now();
    if (now - lastRefreshRef.current < 30000) return; // Min 30s between financial refreshes
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    lastRefreshRef.current = now;
    try {
      if (!mountedRef.current) return;
      await refreshFinancial?.();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Financial refresh error:", err);
      }
    } finally {
      refreshingRef.current = false;
    }
  }, [refreshFinancial]);

  const subscribeProperty = useCallback((propertyId, handlers = {}) => {
    if (!propertyId) return;
    subscribedPropertyRef.current = propertyId;
    if (handlers.onTradeExecuted || handlers.onPriceTick) {
      propertyHandlersRef.current[String(propertyId)] = handlers;
    }
    if (socketRef.current) {
      socketRef.current.emit(SOCKET_EVENTS.SUBSCRIBE_PROPERTY, propertyId);
    }
  }, []);

  const unsubscribeProperty = useCallback((propertyId) => {
    if (socketRef.current && propertyId) {
      socketRef.current.emit(SOCKET_EVENTS.UNSUBSCRIBE_PROPERTY, propertyId);
    }
    delete propertyHandlersRef.current[String(propertyId)];
    if (subscribedPropertyRef.current === propertyId) {
      subscribedPropertyRef.current = null;
    }
  }, []);

  const getPriceTick = useCallback((propertyId) => {
    return priceTicks[propertyId] ?? null;
  }, [priceTicks]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        safeRefreshFinancial();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [safeRefreshFinancial]);

  useEffect(() => {
    if (!user?.id && !user?._id) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnected(false);
      }
      return;
    }

    const url = getSocketUrl();
    if (!url) {
      console.warn("[socket] No API_BASE_URL, skipping Socket.IO");
      return;
    }

    const socket = io(url, {
      path: "/socket.io",
      withCredentials: true,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });
    socketRef.current = socket;
    setSocket(socket);

    const ctx = {
      setConnected,
      setPriceTicks,
      setLastOrderUpdate,
      socket,
      safeRefreshFinancial,
      fetchMe,
      user,
      refreshTimerRef,
      subscribedPropertyRef,
      propertyHandlersRef,
    };
    const listeners = createListeners(ctx);

    Object.entries(listeners).forEach(([event, handler]) => {
      socket.off(event);
      socket.on(event, handler);
    });

    return () => {
      Object.entries(listeners).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setSocket(null);
      setConnected(false);
    };
  }, [user?.id, user?._id, fetchMe, safeRefreshFinancial]);

  const value = {
    connected,
    lastOrderUpdate,
    getPriceTick,
    subscribeProperty,
    unsubscribeProperty,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
